/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { FailureRateMetric } from "components/plan/table/inventory/types";
import { useTenantTranslation } from "hooks/formatters";
import { Layout, LayoutAxis, PlotData } from "plotly.js";
import { useEffect, useState } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import {
  chemicalElementColours,
  defaultLayout,
  fractionTickFormatting,
  generalTickFormatting,
  getLineDash,
  getMarkerSymbol,
  setSelectedDateFromPlotClick,
} from "../plotting";
import { GroupedFailureRateSeries } from "../types";
import { TFunction } from "i18next";
import { useIsAdmin } from "hooks/settings";

interface FailureRatePlotProps {
  failureRateSeries: GroupedFailureRateSeries;
  selectedTimeAggregation: string | null;
  selectedMetric: FailureRateMetric;
  selectedSampleLocation: string | null;
  selectedSamplePositionType: string | null;
  selectedProductGroup: string | null;
  selectedChemicalElements: string[];
  setSelectedDate: (date: Date) => void;
}

export const FailureRatePlot = ({
  failureRateSeries,
  selectedTimeAggregation,
  selectedMetric,
  selectedSampleLocation,
  selectedSamplePositionType,
  selectedProductGroup,
  selectedChemicalElements,
  setSelectedDate,
}: FailureRatePlotProps) => {
  const { t } = useTenantTranslation();
  const isAdmin = useIsAdmin();

  const initialFigData = buildInitialFigData(failureRateSeries, t, isAdmin);

  const [figure, setFigure] = useState(() => ({
    data: initialFigData,
    layout: updateLayout(defaultLayout, selectedMetric, t),
  }));

  useEffect(() => {
    const newData = buildInitialFigData(failureRateSeries, t, isAdmin);
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- merges previous layout state; not purely computed
    setFigure({
      ...figure,
      data: updateFigData(
        newData,
        selectedTimeAggregation,
        selectedMetric,
        selectedSampleLocation,
        selectedSamplePositionType,
        selectedProductGroup,
        selectedChemicalElements
      ),
      layout: updateLayout(figure.layout, selectedMetric, t),
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- figure intentionally excluded: effect calls setFigure, including it causes infinite loop
  }, [
    failureRateSeries,
    selectedTimeAggregation,
    selectedMetric,
    selectedSampleLocation,
    selectedSamplePositionType,
    selectedProductGroup,
    selectedChemicalElements,
    t,
    isAdmin,
  ]);

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={figure.data}
        layout={figure.layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
      <LegendHint />
    </div>
  );
};

const buildInitialFigData = (
  groupedFailureRateSeries: GroupedFailureRateSeries,
  t: TFunction,
  isAdmin: boolean
) => {
  const figData: Partial<PlotData>[] = [];
  for (const [key, series] of groupedFailureRateSeries.values) {
    const colour = chemicalElementColours.get(key.chemicalElementSymbol);

    figData.push({
      x: series.date,
      y: series.failureRate,
      type: "scatter",
      mode: "lines+markers",
      name: t("samples"),
      legendgroup: key.chemicalElementSymbol,
      legendgrouptitle: {
        text: key.chemicalElementSymbol,
      },
      marker: { color: colour, symbol: getMarkerSymbol(0) },
      line: { color: colour, dash: getLineDash(0) },
      customdata: [
        key.timeAggregation,
        "failureRate",
        key.sampleLocation,
        key.samplePositionType,
        key.productGroup,
        key.chemicalElementSymbol,
      ],
    });
    figData.push({
      x: series.date,
      y: series.numFailures,
      type: "scatter",
      mode: "lines+markers",
      name: t("samples"),
      legendgroup: key.chemicalElementSymbol,
      legendgrouptitle: {
        text: key.chemicalElementSymbol,
      },
      marker: { color: colour, symbol: getMarkerSymbol(0) },
      line: { color: colour, dash: getLineDash(0) },
      customdata: [
        key.timeAggregation,
        "numFailures",
        key.sampleLocation,
        key.samplePositionType,
        key.productGroup,
        key.chemicalElementSymbol,
      ],
    });
    if (isAdmin) {
      figData.push({
        x: series.date,
        y: series.expectedFailureRate,
        type: "scatter",
        mode: "lines+markers",
        name: t("expectedLevel2"),
        legendgroup: key.chemicalElementSymbol,
        legendgrouptitle: {
          text: key.chemicalElementSymbol,
        },
        marker: { color: colour, symbol: getMarkerSymbol(1) },
        line: { color: colour, dash: getLineDash(1) },
        customdata: [
          key.timeAggregation,
          "failureRate",
          key.sampleLocation,
          key.samplePositionType,
          key.productGroup,
          key.chemicalElementSymbol,
        ],
      });
      figData.push({
        x: series.date,
        y: series.expectedNumFailures,
        type: "scatter",
        mode: "lines+markers",
        name: t("expectedLevel2"),
        legendgroup: key.chemicalElementSymbol,
        legendgrouptitle: {
          text: key.chemicalElementSymbol,
        },
        marker: { color: colour, symbol: getMarkerSymbol(1) },
        line: { color: colour, dash: getLineDash(1) },
        customdata: [
          key.timeAggregation,
          "numFailures",
          key.sampleLocation,
          key.samplePositionType,
          key.productGroup,
          key.chemicalElementSymbol,
        ],
      });
    }
  }
  return figData;
};

const updateFigData = (
  figData: Partial<PlotData>[],
  selectedTimeAggregation: string | null,
  selectedMetric: FailureRateMetric,
  selectedSampleLocation: string | null,
  selectedSamplePositionType: string | null,
  selectedProductGroup: string | null,
  selectedChemicalElements: string[]
): Partial<PlotData>[] => {
  return figData.map((trace) => {
    const chemicalElementSymbol = trace.customdata?.[5];
    return {
      ...trace,
      visible:
        trace.customdata?.[0] === selectedTimeAggregation &&
        trace.customdata?.[1] === selectedMetric &&
        trace.customdata?.[2] === selectedSampleLocation &&
        trace.customdata?.[3] === selectedSamplePositionType &&
        trace.customdata?.[4] === selectedProductGroup &&
        typeof chemicalElementSymbol === "string" &&
        selectedChemicalElements.includes(chemicalElementSymbol),
    };
  });
};

const updateLayout = (
  currentLayout: Partial<Layout>,
  selectedMetric: FailureRateMetric,
  t: (key: string) => string
): Partial<Layout> => {
  const yaxisUpdate: Partial<LayoutAxis> = {
    ...currentLayout.yaxis,
    title:
      selectedMetric === "failureRate"
        ? { text: t("failureRate") }
        : { text: t("numFailures") },
    tickformat:
      selectedMetric === "failureRate"
        ? fractionTickFormatting
        : generalTickFormatting,
  };

  return {
    ...currentLayout,
    yaxis: yaxisUpdate,
  };
};
